import axios, { AxiosProgressEvent } from 'axios';
import * as authService from '@/utils/authService';

import {
  IArchiveParams,
  IDownloadInfo,
  IDownloadStartInfo,
  IDownloadProgressInfo,
  IDownloadTaskParams,
  IArchiveStreamParams,
  IArchiveStreamResponse,
  IArchiveListResponse,
} from '@/@types/api/videoArchive';

const videoArchiveApi = axios.create({
  baseURL: 'https://video.sm-center.ru',
  headers: {
    client: 'web',
    'Content-Type': 'application/json',
  },
});

videoArchiveApi.interceptors.request.use((config) => {
  config.headers.acx = authService.getAccessToken() || '';
  return config;
});

const controllerArchive = 'Archive';
const downloadManager = 'download-manager';

const getArchiveCalendar = async (
  params: IArchiveParams,
): Promise<IDownloadInfo> => {
  const { data } = await videoArchiveApi.get(`/${controllerArchive}/Calendar`, {
    params: { ...params, acx: authService.getAccessToken() },
  });

  return data;
};

const getArchiveList = async (
  params: IArchiveParams,
): Promise<IArchiveListResponse> => {
  const { data } = await videoArchiveApi.get(`/${controllerArchive}/List`, {
    params,
  });

  return data;
};

const archiveStream = async (
  params: IArchiveStreamParams,
): Promise<IArchiveStreamResponse> => {
  const { data } = await videoArchiveApi.get(
    `/${controllerArchive}/ArchiveStream`,
    { params },
  );

  return data;
};

const downloadInfo = async (params: IArchiveParams): Promise<IDownloadInfo> => {
  const { data } = await videoArchiveApi.get(
    `/${controllerArchive}/DownloadInfo`,
    {
      params: { ...params, acx: authService.getAccessToken() },
    },
  );

  return data;
};
const downloadStart = async (
  params: IArchiveParams,
): Promise<IDownloadStartInfo> => {
  const { data } = await videoArchiveApi.get(`/${downloadManager}/start`, {
    params: { ...params, acx: authService.getAccessToken() },
  });

  return data;
};

const downloadProgress = async ({
  taskID,
}: IDownloadTaskParams): Promise<IDownloadProgressInfo> => {
  const { data } = await videoArchiveApi.get(
    `/${downloadManager}/progress/${taskID}`,
  );

  return data;
};

const getFileNameFromContentDisposition = (
  contentDisposition?: string,
): string => {
  if (!contentDisposition) return 'archive.mp4';

  const encodedFileNameMatch = contentDisposition.match(
    /filename\*=UTF-8''(.+)/i,
  );

  if (encodedFileNameMatch?.[1]) {
    return decodeURIComponent(encodedFileNameMatch[1].replace(/["']/g, ''));
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

  if (fileNameMatch?.[1]) {
    return fileNameMatch[1];
  }

  return 'archive.mp4';
};

const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = blobUrl;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(blobUrl);
};

const downloadFile = async (
  { taskID }: IDownloadTaskParams,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const response = await videoArchiveApi.get<Blob>(
    `/${downloadManager}/file/${taskID}`,
    {
      responseType: 'blob',
      signal,
      onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
        if (!progressEvent.total) return;

        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );

        onProgress?.(percent);
      },
    },
  );

  const contentDisposition = response.headers['content-disposition'];
  const fileName = getFileNameFromContentDisposition(contentDisposition);

  saveBlobAsFile(response.data, fileName);
};

const downloadCancel = async ({
  taskID,
}: IDownloadTaskParams): Promise<IDownloadProgressInfo> => {
  const { data } = await videoArchiveApi.post(
    `/${downloadManager}/cancel/${taskID}`,
  );

  return data;
};

export default {
  getArchiveCalendar,
  getArchiveList,
  downloadInfo,
  downloadStart,
  downloadProgress,
  downloadFile,
  downloadCancel,
  archiveStream
};
